<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import mdiAlertCircle from '~icons/mdi/alert-circle'

import { usePublicToken } from '@/composables/usePublicToken'
import type { PublicOcrNumber } from '@/types/_apiHidden'
import { apiFetch } from '@/utils/api'

// OCR step (ADA-4432 etapp 4) — the LOGGED-OUT entry to the payment flow, reached from the
// login screen or the /ocr/{ocr} deep link. The payer identifies the debt by typing the OCR
// number or the settlement reference number printed on their letter; the server resolves either.
//
// The CANONICAL ocr from the response is what the payment must use — a reference number is not
// a valid OCR for the provider, and the lookup is the only thing that can translate it
// (ADA-4286 B1). That is why this step emits the whole resolved record, not the input.
const { suggestedOcr = '' } = defineProps<{ suggestedOcr?: string }>()
const emit = defineEmits<{ resolved: [PublicOcrNumber] }>()

const { t } = useI18n()
const { getPublicToken } = usePublicToken()

const input = ref(suggestedOcr)
const loading = ref(false)
const error = ref(false)

// Legacy rules_mixin.validate_ocr: non-empty, at most 50 characters. Deliberately loose — the
// field accepts both OCR numbers and reference numbers (which carry a letter prefix and pasted
// spaces), so anything stricter would reject valid input. The server does the real matching.
function validate(value: string): true | string {
  const trimmed = value.trim()
  if (trimmed.length === 0) return t('common.required')
  if (trimmed.length > 50) return t('common.invalid_ocr_number')
  return true
}

const valid = ref(validate(input.value) === true)

watch(input, (value) => {
  error.value = false
  valid.value = validate(value) === true
})

// Latest-lookup-wins (the usePayableAmount pattern): a newer deep link supersedes a lookup
// still in flight, and a superseded response must neither emit nor touch the page state —
// otherwise it would open payment for the OLD number while the field shows the new one.
// Bumping the sequence is also how the step withdraws a lookup it no longer wants an answer to
// (see the watcher and onUnmounted below).
let lookupSequence = 0

async function lookup() {
  if (!valid.value) return
  const sequence = ++lookupSequence
  const number = input.value.trim()
  error.value = false
  loading.value = true
  try {
    const token = await getPublicToken()
    const { data, response } = await apiFetch.request('get', '/public--ocr-numbers/{ocr_or_reference_number}/{token}', {
      params: { path: { ocr_or_reference_number: number, token } },
    })
    if (sequence !== lookupSequence) return
    // The route answers 404 for both an unknown number and one that resolves to no settlement
    // documents — the payer cannot act on the difference, so both read as "not found".
    if (response.status !== 200 || data == null) {
      error.value = true
      return
    }
    emit('resolved', data)
  } catch {
    if (sequence === lookupSequence) error.value = true
  } finally {
    if (sequence === lookupSequence) loading.value = false
  }
}

// Deep link (/ocr/{ocr}): the number came from the URL, so submit it without making the payer
// press Continue on a field they did not fill in (legacy parity).
onMounted(() => {
  if (suggestedOcr.trim() !== '') void lookup()
})

// A second deep link can land while this step is already open — the payload is replaced in
// place, so the page never remounts. Follow the new suggestion the same way the mount does.
watch(
  () => suggestedOcr,
  (value) => {
    // Withdraw the pending lookup FIRST, whatever the new suggestion is worth. Clearing the
    // payload (the dialog being closed mid-lookup) also lands here, and a response arriving
    // after that would still resolve the step — for a debt the payer walked away from.
    // The input keeps its text: a suggestion we reject must not wipe what the payer typed.
    lookupSequence++
    loading.value = false
    if (value.trim() === '' || validate(value) !== true) return
    input.value = value
    valid.value = true
    void lookup()
  }
)

// The request outlives the step: closing the dialog unmounts it while the promise is still
// running, and the payload does not change on every close (the logged-out flow is entered
// without a suggestion at all), so the watcher above cannot be the only withdrawal point.
onUnmounted(() => {
  lookupSequence++
})
</script>

<template>
  <v-sheet class="d-flex flex-column pa-4 pt-2">
    <v-fade-transition>
      <v-alert
        v-show="error"
        :text="t('common.ocr_number_error')"
        :icon="mdiAlertCircle"
        class="mb-6"
        density="compact"
        variant="tonal"
        type="error"
      />
    </v-fade-transition>

    <div class="text-body-large text-medium-emphasis mb-4">{{ t('common.confirm_ocr_number') }}</div>

    <v-text-field
      v-model="input"
      :label="t('common.ocr_or_reference_number')"
      :rules="[validate]"
      :disabled="loading"
      type="text"
      autofocus
      @keyup.enter="lookup"
    />

    <v-divider class="mt-2 mx-n4" />

    <div class="d-flex justify-end mt-4">
      <v-btn
        :disabled="!valid"
        :loading="loading"
        color="primary"
        variant="flat"
        @click="lookup"
      >
        {{ t('common.continue') }}
      </v-btn>
    </div>
  </v-sheet>
</template>
